import { parseAbi, zeroAddress, type Address } from 'viem'
import {
  buildLstWithdrawRequest,
  getLstAcceptedInputs,
  ignoresReceiver,
  type LstAcceptedInput,
} from '@1delta/calldata-sdk'
import { MulticallRetryFunction, GenericTokenList } from '../../types'
import { composeVaultDisplayName } from '../displayName'
import { getLstRegistry, type LstRegistryEntry } from './registry'
import { buildReader, type LstReader } from './readers'
import {
  MULTICALL3_ADDRESS,
  Multicall3BalanceAbi,
  toBigInt,
} from './readers/shared'
import { resolveYieldApr } from './yieldBinding'
import {
  getLstDelegation,
  getLstValidators,
  type LstDelegation,
  type LstValidator,
} from './validators'
import { deriveExitRoutes } from '../exitRoutes'
import type { LstShareToken, LstShareTokens } from './types'

/** 10**18 in bigint — the fixed-point scale of `exchangeRate` (a
 *  decimal-agnostic value ratio), independent of share/underlying decimals. */
const ONE_E18 = 10n ** 18n

const ERC20_BALANCE_ABI = parseAbi([
  'function balanceOf(address) view returns (uint256)',
])

/**
 * Withdrawal modes with a same-block redemption path (the rest —
 * `queued` / `fixed-cooldown` / `dex-only` / `off-chain` — have no
 * instant on-chain exit, so their instant `liquidity` is 0).
 */
const INSTANT_MODES = new Set(['instant-or-queued', 'fee-or-queued'])
/** Reader kinds where the share token is a real ERC-4626, so its idle
 *  balance of the underlying is what a `redeem` can pull this block. */
const IDLE_READER_KINDS = new Set(['erc4626', 'erc4626PreviewRedeem'])

/**
 * Resolve the pay-asset accept-set for an LST. Prefers calldata-sdk's enriched
 * `LST_INPUT_CAPABILITIES` (kinds with alternative inputs / required options);
 * otherwise derives a single entry from the registry's primary
 * `mintInputAsset`. Empty when the share token isn't mintable on-chain.
 */
const resolveAcceptedInputs = (
  chainId: string,
  shareToken: string,
  entry: LstRegistryEntry,
): LstAcceptedInput[] => {
  const enriched = getLstAcceptedInputs(chainId, shareToken as Address)
  if (enriched) return enriched
  if (!entry.isMintable || entry.mintInputAsset === undefined) return []
  return [
    {
      asset:
        entry.mintInputAsset === 'native'
          ? 'native'
          : (entry.mintInputAsset.toLowerCase() as Address),
      mode: 'direct',
    },
  ]
}

/**
 * Does this LST's exit pay anyone other than the caller?
 *
 * Asked of the builder rather than looked up: encode the same exit twice with
 * two receivers and see whether the calldata moves. `true` only when the
 * probe positively shows the receiver is unused — an action that cannot be
 * built for an unrelated reason leaves the flag ABSENT rather than asserting
 * the opposite, since "receiver-capable" is a claim and "we could not tell"
 * is not.
 *
 * The whole point is that a client can see this on the row instead of
 * discovering it from a 400 (or, before the action route gated on it, from a
 * transaction that quietly paid the wrong address).
 */
const probeSelfOnly = (
  chainId: string,
  shareToken: string,
  route: 'instant' | 'queued',
): true | undefined => {
  try {
    const self = ignoresReceiver(buildLstWithdrawRequest, {
      chainId,
      shareToken: shareToken as Address,
      amount: 10n ** 18n,
      receiver: '0x00000000000000000000000000000000000000a1' as Address,
      // Neutral protocol-required inputs, so the probe measures the RECEIVER
      // and not a missing argument.
      options: { route, deadline: 9_999_999_999n, minAssetOut: 0n, minHype: 0n },
    })
    return self ? true : undefined
  } catch {
    return undefined
  }
}

/**
 * One-shot fetcher for all LST share tokens on a chain.
 *
 * Two parallel I/O legs that both feed the same output map:
 *
 *  1. **On-chain leg** — every reader's calls are concatenated into a
 *     single `multicallRetry` payload; the response is sliced back
 *     into per-entry chunks and parsed. Failures in one entry's
 *     parser produce `undefined` and are silently dropped — they
 *     don't sink the whole batch.
 *
 *  2. **APR leg** — `resolveYieldApr` dedups the registry's
 *     `YieldFetcher` refs, runs each once, and resolves the weETH
 *     composition (wstETH base + EtherFi LRT2 bonus).
 *
 * Returns `{}` when the chain has no registry entries. Per-entry
 * failures (multicall slice missing, parser returned `undefined`,
 * APR missing) result in that entry being omitted, never throwing.
 */
export const fetchLstShareTokens = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  opts: {
    /** Fetch the **live** validator set for delegation LSTs (Core, Celo).
     *  These reads go via standalone RPC (`getEvmClient`), separate from the
     *  injected multicall, so they're opt-in to keep the hot data path cheap.
     *  The `delegation` descriptor is always attached regardless. */
    includeValidators?: boolean
  } = {},
): Promise<LstShareTokens> => {
  const entries = getLstRegistry(chainId)
  if (entries.length === 0) return {}

  // Build one reader per entry. Sliced back below by call count.
  const readers: LstReader[] = entries.map(buildReader)
  const allCalls = readers.flatMap((r) => r.calls)
  const allAbis = readers.flatMap((r) => r.abis)

  // Instant-redeemable idle leg: for instant-capable ERC-4626 LSTs whose
  // reader doesn't already measure a buffer, the underlying sitting idle in
  // the vault is what a `redeem` can pull this block. Read it as a separate
  // best-effort multicall — ERC-20 underlyings via `balanceOf(vault)`, native
  // underlyings via `Multicall3.getEthBalance(vault)`. Non-4626 instant LSTs
  // (custom redemption contracts) fall back to 0. Same-chain entries only.
  const idleEntries = entries.filter(
    (e) =>
      INSTANT_MODES.has(e.withdrawalMode) && IDLE_READER_KINDS.has(e.reader),
  )
  const idleIsNative = (e: LstRegistryEntry) =>
    e.underlying.toLowerCase() === zeroAddress
  const idleCalls = idleEntries.map((e) =>
    idleIsNative(e)
      ? {
          address: MULTICALL3_ADDRESS,
          name: 'getEthBalance',
          params: [e.address],
        }
      : { address: e.underlying, name: 'balanceOf', params: [e.address] },
  )
  const idleAbis = idleEntries.map((e) =>
    idleIsNative(e) ? Multicall3BalanceAbi : ERC20_BALANCE_ABI,
  )

  // Group calls by target chain. Most entries call on the request's
  // chain (`call.chainId` undefined). Cross-chain entries (e.g.
  // MaticX-on-Polygon, where the rate-getter lives on Ethereum) tag
  // their rate calls with a `chainId` override; we dispatch those
  // to a separate multicall on that chain in parallel, then stitch
  // the results back into the original positions.
  const groups: Record<
    string,
    { calls: typeof allCalls; abis: typeof allAbis; positions: number[] }
  > = {}
  for (let i = 0; i < allCalls.length; i++) {
    const target = allCalls[i].chainId ?? chainId
    const g =
      groups[target] ??
      (groups[target] = { calls: [], abis: [], positions: [] })
    g.calls.push(allCalls[i])
    g.abis.push(allAbis[i])
    g.positions.push(i)
  }

  // Validator / delegation leg: the few LSTs that let the depositor pick a
  // validator/group/node carry a descriptor (always) plus — for endpoint-
  // sourced ones — the live selectable set. Bounded: only entries that
  // declare a descriptor on this chain do any work; pooled LSTs cost nothing.
  const delegationByAddress = new Map<string, LstDelegation>()
  const validatorJobs: Promise<[string, LstValidator[]]>[] = []
  for (const e of entries) {
    const d = getLstDelegation(chainId, e.address)
    if (!d) continue
    delegationByAddress.set(e.address.toLowerCase(), d)
    if (opts.includeValidators && d.source === 'endpoint') {
      validatorJobs.push(
        getLstValidators(chainId, e.address)
          .then((v) => [e.address.toLowerCase(), v] as [string, LstValidator[]])
          .catch(
            () => [e.address.toLowerCase(), []] as [string, LstValidator[]],
          ),
      )
    }
  }

  // Run all chains' multicalls in parallel with the APR + idle + validator legs.
  const chainEntries = Object.entries(groups)
  const [perChainResults, aprByAddress, idleResults, validatorEntries] =
    await Promise.all([
      chainEntries.length > 0
        ? Promise.all(
            chainEntries.map(async ([chain, { calls, abis }]) => ({
              chain,
              results: (await multicallRetry({
                chain,
                calls: calls as unknown as never[],
                abi: abis as unknown as never,
              })) as unknown[],
            })),
          )
        : Promise.resolve([] as { chain: string; results: unknown[] }[]),
      resolveYieldApr(entries),
      idleEntries.length > 0
        ? (multicallRetry({
            chain: chainId,
            calls: idleCalls as unknown as never[],
            abi: idleAbis as unknown as never,
            allowFailure: true,
          }) as Promise<unknown[]>)
        : Promise.resolve([] as unknown[]),
      Promise.all(validatorJobs),
    ])
  const validatorsByAddress = new Map<string, LstValidator[]>(validatorEntries)

  // Map each idle-capable entry's address → its idle underlying balance.
  const idleByAddress = new Map<string, bigint>()
  idleEntries.forEach((e, i) => {
    const bal = toBigInt(idleResults[i])
    if (bal !== undefined) idleByAddress.set(e.address.toLowerCase(), bal)
  })

  // Stitch per-chain results back into a flat array indexed by the
  // original `allCalls` position.
  const rawResults: unknown[] = new Array(allCalls.length)
  for (const { chain, results } of perChainResults) {
    const positions = groups[chain].positions
    for (let i = 0; i < positions.length; i++) {
      rawResults[positions[i]] = results[i]
    }
  }

  const out: LstShareTokens = {}
  let cursor = 0
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    const reader = readers[i]
    const slice = rawResults.slice(cursor, cursor + reader.calls.length)
    cursor += reader.calls.length

    const state = reader.parse(slice)
    if (!state) continue

    const addressLc = entry.address.toLowerCase()
    const underlyingLc = entry.underlying.toLowerCase()
    const asset = tokenList[underlyingLc]
    // Price by `priceUnderlying` when set (an economically-equivalent priced
    // asset for an unpriced pay asset, e.g. BGT wrappers → WBERA), else by the
    // pay asset itself. `asset`/deposits still use `underlying` (the pay asset).
    const priceUsd =
      prices[entry.priceUnderlying?.toLowerCase() ?? underlyingLc]
    const supplyRate = aprByAddress[addressLc] ?? 0
    const rewardsRate = 0
    const depositRate = supplyRate + rewardsRate

    // Share vs underlying decimals — they diverge only for a handful of
    // entries (e.g. 18-dec SolvBTC over 8-dec WBTC). `totalAssets` and
    // `liquidity` are in raw UNDERLYING units, so format with the
    // underlying decimals; `exchangeRate` is the decimal-agnostic 1e18
    // value ratio. Mirrors the savings provider.
    const shareDec = entry.decimals
    const underlyingDec = entry.underlyingDecimals ?? entry.decimals
    const shareUnit = 10n ** BigInt(shareDec)
    const underlyingUnit = 10n ** BigInt(underlyingDec)

    const totalAssetsFormatted = Number(state.totalAssets) / 10 ** underlyingDec
    const totalAssetsUsd =
      priceUsd !== undefined ? totalAssetsFormatted * priceUsd : 0

    // ERC-4626 semantics, decimal-correct across share ≠ underlying:
    //   convertToAssets(shareUnit raw shares) → raw underlying
    //     = exchangeRate * underlyingUnit / 1e18
    //   convertToShares(underlyingUnit raw underlying) → raw shares
    //     = 1e18 * shareUnit / exchangeRate
    const convertToAssets = (state.exchangeRate * underlyingUnit) / ONE_E18
    const convertToShares =
      state.exchangeRate > 0n ? (ONE_E18 * shareUnit) / state.exchangeRate : 0n

    // Instantly-withdrawable underlying right now, always populated (parity
    // with the 4626 vault providers). Priority:
    //   1. a reader-measured protocol buffer (rETH deposit pool, stS pool);
    //   2. for instant-capable ERC-4626 LSTs, the idle underlying in the
    //      vault (what a `redeem` can pull this block), clamped to totalAssets;
    //   3. otherwise 0 — queue/cooldown/dex-only/off-chain LSTs have no
    //      same-block on-chain redemption.
    let liquidityRaw: bigint
    if (state.liquidity !== undefined) {
      liquidityRaw = state.liquidity
    } else {
      const idle = idleByAddress.get(addressLc)
      liquidityRaw =
        idle !== undefined
          ? idle > state.totalAssets
            ? state.totalAssets
            : idle
          : 0n
    }
    const liquidityFormatted = Number(liquidityRaw) / 10 ** underlyingDec
    const liquidityUsd =
      priceUsd !== undefined ? liquidityFormatted * priceUsd : 0

    const cooldownSecs =
      state.withdrawalCooldownSeconds ?? entry.withdrawalCooldownSeconds

    const displayName = composeVaultDisplayName(
      entry.brand,
      entry.brand,
      asset ?? {
        chainId,
        address: underlyingLc,
        symbol: entry.symbol,
        decimals: entry.decimals,
      },
      `${entry.brand} ${entry.symbol}`,
    )

    const token: LstShareToken = {
      address: addressLc,
      underlying: underlyingLc,
      symbol: entry.symbol,
      name: `${entry.brand} ${entry.symbol}`,
      displayName,
      brand: entry.brand,
      curatorName: entry.brand,
      decimals: entry.decimals,
      assetDecimals: underlyingDec,
      totalAssets: state.totalAssets.toString(),
      totalSupply: state.totalSupply.toString(),
      convertToShares: convertToShares.toString(),
      convertToAssets: convertToAssets.toString(),
      exchangeRate: state.exchangeRate.toString(),
      supplyRate,
      rewardsRate,
      depositRate,
      isErc4626: entry.isErc4626,
      isRebasing: entry.isRebasing,
      isMintable: entry.isMintable,
      isNativeUnderlying: entry.isNativeUnderlying,
      mintContract: entry.mintContract?.toLowerCase(),
      mintInputAsset:
        entry.mintInputAsset === undefined
          ? undefined
          : entry.mintInputAsset === 'native'
            ? 'native'
            : entry.mintInputAsset.toLowerCase(),
      acceptedInputs: resolveAcceptedInputs(chainId, addressLc, entry),
      delegation: delegationByAddress.get(addressLc),
      validators: validatorsByAddress.get(addressLc),
      withdrawalMode: entry.withdrawalMode,
      // A reader-measured wait wins over the registry's static one — see
      // `LstOnChainState.withdrawalCooldownSeconds`.
      withdrawalCooldownSeconds: cooldownSecs,
      withdrawFeeBps: state.withdrawFeeBps,
      // A reader that reads its protocol's own per-leg dials publishes the
      // list itself; everything else gets the coarse derivation, which is
      // still strictly better than making every consumer reassemble the two
      // legs out of `withdrawalMode` + one cooldown + one liquidity number.
      exitRoutes: (
        state.exitRoutes ??
        deriveExitRoutes({
          withdrawalMode: entry.withdrawalMode,
          withdrawFeeBps: state.withdrawFeeBps,
          withdrawalCooldownSeconds: cooldownSecs,
          liquidity: liquidityRaw.toString(),
          liquidityFormatted,
          liquidityUsd,
        })
      ).map((r) =>
        // A reader that already established it wins; otherwise probe.
        r.selfOnly !== undefined || r.kind === 'market'
          ? r
          : {
              ...r,
              selfOnly: probeSelfOnly(
                chainId,
                addressLc,
                r.kind === 'instant' ? 'instant' : 'queued',
              ),
            },
      ),
      asset,
      priceUsd,
      totalAssetsFormatted,
      totalAssetsUsd,
      liquidity: liquidityRaw.toString(),
      liquidityFormatted,
      liquidityUsd,
    }
    out[addressLc] = token
  }

  return out
}
